import {Injectable}                                from '@angular/core';
import {
  FormDataMonitoringTypeData,
  MonitoringDetailCustomData,
  MonitoringDetailEntryFormData
}                                                  from './monitoring-detail-entry.types';
import {Observable}                                from 'rxjs';
import {
  ApDynformsConfigFieldset
}                                                  from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigComboBox
}                                                  from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {nameOf}                                    from '../../../ap-core/utils/ap-name-of';
import {ApDynformsValidator}                       from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                                from '@angular/forms';
import {
  ApDynformsConfigDatepicker
}                                                  from '../../../ap-dynforms/config/ap-dynforms-config-datepicker';
import {
  ApDynformsConfigNumerictextbox
}                                                  from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {
  ApDynformsConfigPlaceholder
}                                                  from '../../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {distinctUntilChanged, map}                 from 'rxjs/operators';
import {MonitoringTypeStore}                       from '../../../stores/fields/monitoring-type.store';
import {UnitService}                               from '../../../services/data/unit.service';
import {
  ApDynformsConfigTextarea
}                                                  from '../../../ap-dynforms/config/ap-dynforms-config-textarea';
import {
  ApDynformsConfigUpload
}                                                  from '../../../ap-dynforms/config/ap-dynforms-config-upload';
import {ApHelpImageUpload}                         from '../../../help/components/ap-help-image.upload';
import {FileState}                                 from '@progress/kendo-angular-upload';
import {
  MonitoringDetailStore
}                                                  from '../../../stores/fields/monitoring-detail.store';
import {ApDynformsComponent}                       from '../../../ap-dynforms/ap-dynforms.component';
import {FieldStore}                                from '../../../stores/farm/field.store';
import {
  MonitoringRulesStore
}                                                  from '../../../stores/agronomic/monitoring-rules.store';
import {ApGetCropService}                          from '../../../stores/services/ap-get-crop.service';
import {FileService}                               from '../../../services/common/file.service';
import {
  IModalDialogResult
}                                                  from '../../../ap-interface/interfaces/ap-modaldialog-data.interface';
import {ModalDialogButtonKeys, ModalDialogPresets} from '../../../stores/dialog/modal.dialog.presets';
import {ModalDialogStore}                          from '../../../stores/dialog/modal.dialog.store';
import {
  Base64FileData,
  FileInfoData,
  UploadFileEvent
}                                                  from '../../../ap-dynforms/types/ap-file-upload.types';
import {
  ApFileUploadComponentService
}                                                  from '../../../ap-dynforms/services/ap-file-upload-component.service';
import {ApDateService}                             from '../../../ap-core/services/ap-date-service';
import {UploadFileService}                         from '../../../services/common/upload-file.service';
import {CampaignYearService}                       from '../../../services/data/campaign-year.service';
import {ObjectFactory}                             from 'ts-tooling';
import IGuid = System.IGuid;
import IMonitoringType = Data.Fields.IMonitoringType;
import ICampaignYear = Data.Authentication.ICampaignYear;
import IMonitoringDetail = Data.Fields.IMonitoringDetail;
import IMonitoringDetailAttachment = Data.Fields.IMonitoringDetailAttachment;
import IMonitoringDetailAttachmentWithoutContent = Data.Fields.IMonitoringDetailAttachmentWithoutContent;

/**
 * Service for managing monitoring detail entry configuration and operations
 * Handles form generation, file uploads, and monitoring recommendations
 */
@Injectable()
export class MonitoringDetailEntryConfig {

  constructor(private fieldStore: FieldStore,
              private modalDialogStore: ModalDialogStore,
              private modalDialogPresets: ModalDialogPresets,
              private monitoringTypeStore: MonitoringTypeStore,
              private monitoringRulesStore: MonitoringRulesStore,
              private monitoringDetailStore: MonitoringDetailStore,
              private fileUploadComponentService: ApFileUploadComponentService,
              private cropService: ApGetCropService,
              private unitService: UnitService,
              private fileService: FileService,
              private dateService: ApDateService,
              private uploadFileService: UploadFileService,
              private campaignYearService: CampaignYearService) {
  }

  /**
   * Handles form value changes for monitoring detail fields
   * Performs specific actions when monitoring type or value fields change:
   * - When monitoring type changes: resets dependent fields (recommendation, value)
   * - When value field changes to non-numeric: clears recommendation
   * @param dynForm The entry component's dynForm instance
   * @param valuesChangesArgs The form's valueChanged event arguments containing control name and new value
   */
  public handleMonitoringChanges(dynForm: ApDynformsComponent, valuesChangesArgs: { control: string; value: any }): void {
    if (!dynForm?.form?.dirty && !dynForm?.form?.touched) {
      return;
    }

    const formData = dynForm?.form.getRawValue() as MonitoringDetailEntryFormData;

    if (valuesChangesArgs.control === nameOf<MonitoringDetailEntryFormData>('MonitoringTypeId')) {
      formData.Recommendation = undefined;
      formData.Value = undefined;
      this.patchForm(dynForm, formData, true);
      return;
    }

    if (valuesChangesArgs.control === nameOf<MonitoringDetailEntryFormData>('Value')
      && !Number.isFinite(formData.Value)) {
      formData.Recommendation = undefined;
      this.patchForm(dynForm, formData, true);
    }
  }

  /**
   * Calculates and updates the recommendation value based on monitoring data changes.
   * Only processes calculations when:
   * - The form has been modified (dirty/touched)
   * - Both EcValue and Value fields contain valid numeric values
   * - The monitoring type supports recommendations
   * - The field has a valid crop with an eppoCode
   * @param dynForm The dynamic form component instance
   * @param fieldId The ID of the monitoring field
   * @param valuesChangesArgs The form's value change event arguments
   */
  public handleMonitoringRecommendation(dynForm: ApDynformsComponent, fieldId: IGuid,
                                        valuesChangesArgs: { control: string; value: any }): void {
    if (dynForm?.form?.pristine || (!dynForm?.form?.dirty && !dynForm?.form?.touched)) {
      return;
    }

    const formData = dynForm?.form.getRawValue() as MonitoringDetailEntryFormData;

    if ((valuesChangesArgs.control === nameOf<MonitoringDetailEntryFormData>('EcValue') ||
        valuesChangesArgs.control === nameOf<MonitoringDetailEntryFormData>('Value'))
      && Number.isFinite(formData.EcValue)
      && Number.isFinite(formData.Value)
      && this.isMonitoringRecommendationExist(formData.MonitoringTypeId)) {

      const crop = this.cropService.getCropType(
        this.cropService.getFieldCrop(this.fieldStore.getFieldById(fieldId)),
        true);

      if (!crop?.Eppocode) {
        return;
      }

      this.monitoringRulesStore.calculateRecommendation(formData.Value, crop.Eppocode, formData.EcValue, formData.MonitoringTypeId)
        .then(recommendationValue => {
          // As per definition -1 is returned if no recommendation rule could be found for the given
          // combination of monitoring data => skip such values.
          // Recommendation values are always >= 0
          if (recommendationValue < 0) {
            return;
          }
          const formDataRecommendationCalculation = dynForm?.form.getRawValue() as MonitoringDetailEntryFormData;
          formDataRecommendationCalculation.Recommendation = recommendationValue;
          this.patchForm(dynForm, formDataRecommendationCalculation, true);
        });
    }
  }

  /**
   * Generates a complete form configuration with field sets for monitoring detail entry.
   * @param formData Initial form data values.
   * @param formData$ Observable stream of form data changes.
   * @returns Array of fieldset configurations.
   * @public
   */
  public generateFormConfig(formData: MonitoringDetailEntryFormData, formData$: Observable<any>): ApDynformsConfigFieldset[] {
    return [
      new ApDynformsConfigFieldset({
        key: 'Global__General_Information',
        legend: 'Global__General_Information',
        config: [
          new ApDynformsConfigDatepicker({
            key: nameOf<MonitoringDetailEntryFormData>('Date'),
            label: 'Nutrients__FertilizationDate',
            minDate: formData.MinDateRange,
            maxDate: formData.MaxDateRange,
            infoText: '',
            value: formData.Date,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<MonitoringDetailEntryFormData>('EcValue'),
            label: 'Global__ECStage',
            min: 0,
            max: 99,
            decimals: 0,
            format: 'n0',
            step: 1,
            value: formData?.EcValue,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'Worktype_Category_Valuation',
        legend: 'Worktype_Category_Valuation',
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<MonitoringDetailEntryFormData>('MonitoringTypeId'),
            label: 'Global_Type',
            valueField: nameOf<IMonitoringType>('Id'),
            textField: nameOf<IMonitoringType>('TranslationKey'),
            translate: true,
            value: formData?.MonitoringTypeId,
            options: this.monitoringTypeStore.MonitoringTypes$,
            valuePrimitive: true,
            sort: 1,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigPlaceholder(),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<MonitoringDetailEntryFormData>('Value'),
            label: 'NMonitoring__MeasuredValue',
            label$: this._formDataMonitoringTypeObservable(formData$, (data) => {
              return this.unitService.getUnitWithLabelKey(data.MonitoringType?.ValueUnit, 'NMonitoring__MeasuredValue', false, false);
            }),
            min: 0,
            max: 9999,
            decimals: 0,
            format: 'n0',
            maximumFractionDigits: 2,
            value: formData?.Value,
            value$: this._formDataMonitoringTypeObservable(formData$, (data) => {
              return !!data.MonitoringType?.Value ? data.FormValues.Value : undefined;
            }),
            disabled: formData?.IsValueDisabled ?? true,
            disabled$: this._formDataMonitoringTypeObservable(formData$, (data) => {
              return !!data.MonitoringType ? !data.MonitoringType.Value : true;
            }),
            validators: [
              new ApDynformsValidator({
                errorKey: 'Settings__Msg_Vali_Value_Required',
                validator: Validators.required
              })
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<MonitoringDetailEntryFormData>('Recommendation'),
            label: 'Nutrients__Recommendation',
            label$: this._formDataMonitoringTypeObservable(formData$, (data) => {
              return this.unitService.getUnitWithLabelKey(data.MonitoringType?.RecommendationUnit, 'Nutrients__Recommendation', false, false);
            }),
            min: 0,
            max: 9999,
            decimals: 0,
            format: 'n0',
            maximumFractionDigits: 2,
            value: formData?.Recommendation,
            value$: this._formDataMonitoringTypeObservable(formData$, (data) => {
              return !!data.MonitoringType?.Recommendation ? data.FormValues.Recommendation : undefined;
            }),
            disabled: formData?.IsRecommendationDisabled ?? true,
            disabled$: this._formDataMonitoringTypeObservable(formData$, (data) => {
              return !!data.MonitoringType ? !data.MonitoringType.Recommendation : true;
            }),
            validators: [
              new ApDynformsValidator({
                errorKey: 'Settings__Msg_Vali_Value_Required',
                validator: Validators.required
              })
            ]
          }),
          new ApDynformsConfigTextarea({
            key: nameOf<MonitoringDetailEntryFormData>('Notes'),
            label: 'Global__Note',
            value: formData?.Notes,
            rows: 4
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'FilesToUpload',
        legend: 'FilesToUpload',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigUpload({
            key: 'Attachments',
            value: formData?.Attachments,
            autoUpload: false,
            multiple: true,
            allowDownload: true,
            onlyUniqueFiles: true,
            showFilePreview: true,
            validateWithForm: true,
            restrictions: [...ApHelpImageUpload.AllowedExtensions, '.webm', '.mp4', '.mov', '.avi', '.pdf'],
            maxFileSize: 31 * 1024 * 1024,
            uploadEventHandler: this._uploadFile.bind(this),
            downloadFileCallback: this._downloadFileContent.bind(this)
          })
        ]
      })
    ];
  }

  /**
   * Creates form a data object from campaign year and monitoring detail.
   * Initializes a form with existing data or defaults for new entries.
   * @param campaignYear Current campaign year for date range.
   * @param monitoringDetail Optional existing monitoring detail.
   * @param monitoringTypes Collection of monitoring types.
   * @returns Form data object with initial values and field states.
   * @public
   */
  public getFormData(campaignYear: ICampaignYear, monitoringDetail: IMonitoringDetail | undefined, monitoringTypes: IMonitoringType[]): MonitoringDetailEntryFormData {
    const monitoringType = monitoringTypes.find(x => x.Id === monitoringDetail?.TypeId);
    const campaignYearRange = this.campaignYearService.getCampaignYearRange(campaignYear.Year);
    return {
      MonitoringTypeId: monitoringDetail?.TypeId,
      Date: monitoringDetail?.Date ? new Date(monitoringDetail?.Date) : new Date(),
      MinDateRange: campaignYearRange.StartYear,
      MaxDateRange: campaignYearRange.EndYear,
      EcValue: monitoringDetail?.EcStage,
      Value: monitoringDetail?.Value,
      IsValueDisabled: monitoringType ? !monitoringType.Value : true,
      Recommendation: monitoringDetail?.Recommendation,
      IsRecommendationDisabled: monitoringType ? !monitoringType.Recommendation : true,
      Notes: monitoringDetail?.Notes,
      Attachments: monitoringDetail?.Attachments?.map(x => this._convertAttachmentToFileInfoData(x)) ?? []
    };
  }

  /**
   * Displays confirmation dialog for unsaved changes
   * @param onConfirmed Callback to execute when confirmed
   * @public
   */
  public onDeclineUnsavedChanges(onConfirmed: () => void): void {
    this.modalDialogStore.setModalDialogData(this.modalDialogPresets.UnsavedChangesDialog({
      resultDelegate: (dialogResult: IModalDialogResult): void => {
        if (dialogResult?.key === ModalDialogButtonKeys.Confirm) {
          onConfirmed();
        }
      }
    }));
  }

  /**
   * Maps form data to backend monitoring detail object.
   * Creates new or updates existing monitoring detail with form values.
   * @param formData - Current form data values
   * @param monitoringDetail - Existing monitoring detail to update (optional)
   * @param customData - Additional custom data for monitoring detail
   * @returns Updated or new monitoring detail object
   */
  public applyFormDataToBackendObject(formData: MonitoringDetailEntryFormData | undefined,
                                      monitoringDetail: IMonitoringDetail | undefined,
                                      customData: MonitoringDetailCustomData | undefined): IMonitoringDetail | undefined {
    if (!formData) {
      return undefined;
    }
    const copiedFormData = ObjectFactory.Copy(formData);
    let newMonitoringDetail: IMonitoringDetail;
    const attachments = copiedFormData.Attachments?.map(file => ({Id: file.uid as IGuid} as IMonitoringDetailAttachmentWithoutContent)) ?? [];
    const monitoringType = this.monitoringTypeStore.MonitoringTypes.find(x => x.Id === copiedFormData.MonitoringTypeId);
    const position = !!customData?.Position ? customData.Position[Object.keys(customData.Position)[0]] as any : null;
    if (!!monitoringDetail) {
      newMonitoringDetail = ObjectFactory.Copy(monitoringDetail);
      newMonitoringDetail.TypeId = monitoringType.Id;
      newMonitoringDetail.Date = copiedFormData.Date;
      newMonitoringDetail.EcStage = copiedFormData.EcValue;
      newMonitoringDetail.Notes = copiedFormData.Notes;
      newMonitoringDetail.Value = copiedFormData.Value;
      newMonitoringDetail.ValueUnit = monitoringType.ValueUnit;
      newMonitoringDetail.Recommendation = copiedFormData.Recommendation;
      newMonitoringDetail.RecommendationUnit = monitoringType.RecommendationUnit;
      newMonitoringDetail.Position = position;
      newMonitoringDetail.Attachments = attachments;
    } else {
      newMonitoringDetail = {
        MonitoringFieldId: customData?.MonitoringFieldId,
        TypeId: monitoringType.Id,
        Date: copiedFormData.Date,
        EcStage: copiedFormData.EcValue,
        Notes: copiedFormData.Notes,
        Value: copiedFormData.Value,
        ValueUnit: monitoringType.ValueUnit,
        Recommendation: copiedFormData.Recommendation,
        RecommendationUnit: monitoringType.RecommendationUnit,
        Position: position,
        Attachments: attachments
      } as IMonitoringDetail;
    }
    return newMonitoringDetail;
  }

  /**
   * Checks if recommendation rules exist for the specified monitoring type
   * @param monitoringTypeId The ID of the monitoring type to check
   * @returns True if the monitoring type has recommendation rules, false otherwise
   * @private
   */
  private isMonitoringRecommendationExist(monitoringTypeId: number | undefined): boolean {
    return !!this.monitoringTypeStore.MonitoringTypes?.FirstOrDefault(t => t.Id === monitoringTypeId)?.RulesRecommendation;
  }

  /**
   * Updates a dynamic form with new values without triggering change events
   * Uses setTimeout to ensure updates happen in the next event cycle
   * @param dynForm The dynamic form component to update
   * @param formData The form data values to apply
   * @param triggerValidation Whether to update validation checks
   * @private
   */
  private patchForm(dynForm: ApDynformsComponent | undefined, formData: MonitoringDetailEntryFormData | undefined,
                    triggerValidation?: boolean): void {
    if (!dynForm || !formData) {
      return;
    }
    setTimeout(() => {
      const { [nameOf<MonitoringDetailEntryFormData>('Attachments')]: _, ...updatedFormData } = formData; // Avoid updating attachments
      dynForm.form.patchValue(updatedFormData, {emitEvent: false});
      if (triggerValidation) {
        setTimeout(() => dynForm.form.updateValueAndValidity(), 0);
      }
    }, 0);
  }

  /**
   * Downloads file content for a given file ID
   * @param fileId Unique identifier of the file to download
   * @param callback Function to handle downloaded file data
   * @private
   */
  private _downloadFileContent(fileId: string, callback: (base64FileData: Base64FileData | undefined) => void): void {
    this.monitoringDetailStore.downloadAttachment(fileId as IGuid)
      .then((attachment) => {
        try {
          callback({
            FileName: attachment.Name,
            MimeType: attachment.ContentMimeType,
            Size: attachment.ContentSize,
            Base64: attachment.ContentBase64
          });
        } catch (e) {
          console.warn(e);
          callback(undefined);
        }
      })
      .catch(() => {
        console.warn('Attachment could not be downloaded');
        callback(undefined);
      });
  }

  /**
   * Handles file upload event initiation
   * @param event Upload event containing files to process
   * @private
   */
  private _uploadFile(event: UploadFileEvent): void {
    if (!event?.files && event.files.length <= 0) {
      return;
    }
    this._processFileUploading(event).then();
  }

  /**
   * Converts a monitoring detail attachment to a FileInfo object compatible with the upload component.
   * @param attachment Monitoring detail attachment to convert
   * @returns FileData object with file metadata and content
   * @private
   */
  private _convertAttachmentToFileInfoData(attachment: IMonitoringDetailAttachmentWithoutContent): FileInfoData {
    const uploadDate = !!attachment.CreatedAt
      ? this.dateService.toFarmDate(new Date(attachment.CreatedAt)).toDate()
      : undefined;
    const thumbnailExtension = this.fileService.getFileExtensionByMimeType(attachment.ThumbnailMimeType);
    const fileInfoData = this.fileUploadComponentService.createFileInfoData(
      attachment.Id.toString(), uploadDate,
      {
        FileName: attachment.Name,
        MimeType: attachment.ContentMimeType,
        Size: attachment.ContentSize,
        Base64: null
      },
      {
        FileName: `thumbnail.${thumbnailExtension}`,
        MimeType: attachment.ThumbnailMimeType,
        Size: attachment.ThumbnailSize,
        Base64: attachment.ThumbnailBase64
      });
    fileInfoData.state = FileState.Uploaded;
    return fileInfoData;
  }

  /**
   * Creates an observable that processes form data changes related to monitoring type.
   * @param formData$ Source form data observable.
   * @param callback Function to transform monitoring type data.
   * @returns Observable of processed form data based on monitoring type changes.
   * @public
   */
  private _formDataMonitoringTypeObservable(formData$: Observable<any>, callback: (data: FormDataMonitoringTypeData) => any): Observable<any> {
    return formData$.pipe(
      distinctUntilChanged((previous, current) => previous.MonitoringTypeId === current.MonitoringTypeId),
      map(formValues => ({
        FormValues: formValues,
        MonitoringType: this.monitoringTypeStore.MonitoringTypes.find(y => y.Id === formValues.MonitoringTypeId)
      } as FormDataMonitoringTypeData)),
      map(x => callback(x))
    );
  }

  /**
   * Processes file upload events and manages to upload progress
   * @param event Upload event containing file information and process controls
   * @private
   */
  private async _processFileUploading(event: UploadFileEvent): Promise<void> {
    if (event.files.length <= 0) {
      return;
    }
    for (const fileDataInfo of event.files) {
      if (fileDataInfo.state === FileState.Uploading || fileDataInfo.state === FileState.Uploaded) {
        return;
      }
      try {
        const thumbnailFileDataUrl = await this.fileService.readFileToDataUrl(fileDataInfo.ThumbnailFile);
        const rawFileDataUrl = await this.fileService.readFileToDataUrl(fileDataInfo.rawFile);
        const attachment = {
          Id: fileDataInfo.uid as IGuid,
          Name: fileDataInfo.name,
          ContentSize: fileDataInfo.rawFile.size,
          ContentMimeType: fileDataInfo.rawFile.type,
          ContentBase64: this.fileService.getBase64FromDataUrl(rawFileDataUrl)
        } as IMonitoringDetailAttachment;
        if (!!thumbnailFileDataUrl) {
          attachment.ThumbnailSize = fileDataInfo.ThumbnailFile?.size;
          attachment.ThumbnailMimeType = fileDataInfo.ThumbnailFile?.type;
          attachment.ThumbnailBase64 = this.fileService.getBase64FromDataUrl(thumbnailFileDataUrl);
        }
        event.fileProcess.setUploadProgress(fileDataInfo.uid, 0);
        let shouldProgressStopProgress = false;
        this.uploadFileService.controlUploadProgress((progress: number) => {
          event.fileProcess.setUploadProgress(fileDataInfo.uid, progress);
          return shouldProgressStopProgress;
        }, {Interval: 100, Increment: 9});
        this.monitoringDetailStore.uploadAttachment(attachment)
          .then(() => {
            shouldProgressStopProgress = true;
            event.fileProcess.setUploadProgress(fileDataInfo.uid, 100);
            event.fileProcess.setUploadFinished(fileDataInfo.uid);
          })
          .catch(() => {
            console.warn('Error during uploading attachment');
            shouldProgressStopProgress = true;
            event.fileProcess.setUploadFailed(fileDataInfo.uid);
          });
      } catch (ex) {
        console.warn(ex);
        event.fileProcess.setUploadFailed(fileDataInfo.uid);
      }
    }
  }
}
